import { ChangeDetectionStrategy, Component, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MaterialModule } from 'src/app/shared/material/material.module';
import { NavigationEnd, Router, RouterModule } from '@angular/router';
import { combineLatest, debounceTime, filter, fromEvent, map, startWith, tap } from 'rxjs';
import { MatDrawerMode } from '@angular/material/sidenav';
import { LayoutHeaderComponent } from './layout-header/layout-header.component';
import { LayoutMenuComponent } from './layout-menu/layout-menu.component';
import { LayoutFooterComponent } from './layout-footer/layout-footer.component';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';

@Component({
  selector: 'app-layout',
  standalone: true,
  imports: [
    CommonModule,
    MaterialModule,
    LayoutFooterComponent,
    LayoutHeaderComponent,
    LayoutMenuComponent,
    RouterModule,
  ],
  templateUrl: './layout.component.html',
  styleUrls: ['./layout.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LayoutComponent {
  protected readonly mode = signal<MatDrawerMode>('side');
  protected readonly open = signal(false);
  protected readonly breakpoint: number = 1280;
  protected readonly closeMenu$ = this.router.events.pipe(
    takeUntilDestroyed(),
    filter((event) => event instanceof NavigationEnd),
    filter(() => window.innerWidth < this.breakpoint),
    tap(() => this.open.set(false))
  );
  protected readonly resizeMenu$ = fromEvent(window, 'resize').pipe(
    takeUntilDestroyed(),
    startWith(window.innerWidth),
    debounceTime(100),
    map((event: Event | number) => {
      if (event instanceof Event) {
        const target = event.target as Window;
        return target.innerWidth;
      }
      return event;
    }),
    tap((width) => {
      if (width < this.breakpoint) {
        this.mode.set('over');
        this.open.set(false);
      } else {
        this.mode.set('side');
        this.open.set(true);
      }
    })
  );
  protected readonly autosubscribe = toSignal(combineLatest([this.closeMenu$, this.resizeMenu$]));

  constructor(private readonly router: Router) {}

  protected toggleMenu = () => {
    const value = this.open();
    this.open.set(!value);
  };

  protected closeMenu = () => {
    this.open.set(false);
  };
}
